import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { ButtonSize, ButtonState, ButtonType, InputSize, InputState } from '../../../../../../assets/types/dtoTypes';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { RegistrationPageService } from '../../registration-page.service';
import { LanguageDataService } from '../../../../../shared/services/language-data.service';
import { ButtonComponent } from '../../../../../shared/button/button.component';
import { TranslateModule } from '@ngx-translate/core';
import { FlexModule } from '@angular/flex-layout';
import { NgIf } from '@angular/common';
import { MatFormField, MatError, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { DropdownComponent } from '../../../../../shared/countries-dropdown/dropdown.component';
import {InputComponent} from "../../../../../shared/input/input.component";

function nonEmptyTrimmed(control: AbstractControl): ValidationErrors | null {
  const v = (control.value ?? '').toString().trim();
  return v.length ? null : { nonEmpty: true };
}

@Component({
  selector: 'app-personal-information-form',
  templateUrl: './personal-information-form.component.html',
  styleUrls: ['./personal-information-form.component.scss'],
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    FlexModule,
    MatFormField, MatError, MatHint, MatLabel,
    MatInput,
    MatButtonToggleGroup, MatButtonToggle,
    DropdownComponent,
    NgIf, InputComponent
  ],
  standalone: true
})
export class PersonalInformationFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dataService = inject(LanguageDataService);
  private registrationPageService = inject(RegistrationPageService);
  private cdr = inject(ChangeDetectorRef);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
  protected readonly InputState = InputState;
  protected readonly InputSize = InputSize;

  countries: any[] = [];
  form!: FormGroup;

  isCodeValid = false;
  codeValidated = false;
  isCodeChecking = false;

  ngOnInit() {
    // Build the form FIRST
    this.form = this.fb.group({
      country: new FormControl('', [Validators.required]),
      lookingFor: new FormControl<'AUSBILDUNG_TRAINING' | 'FACHKRAFT_WORK' | ''>('', [Validators.required]),
      domain: new FormControl<string>('', [Validators.required, Validators.maxLength(120), nonEmptyTrimmed]),
      referralSource: new FormControl<'partner' | 'self'>('self', [Validators.required]),
      partnerCode: new FormControl<string>('')
    });

    this.dataService.getCountries().subscribe(countries => {
      this.countries = countries || [];
      const tunisia = this.countries.find((c: any) => c.name === 'Tunisia');
      if (tunisia) this.form.patchValue({ country: tunisia });
      this.cdr.detectChanges();
    });

    // Partner code validator toggle
    this.form.get('referralSource')?.valueChanges.subscribe(value => {
      if (value === 'partner') {
        this.form.get('partnerCode')?.setValidators([Validators.required]);
      } else {
        this.form.get('partnerCode')?.clearValidators();
        this.form.get('partnerCode')?.setValue('');
        this.isCodeValid = true;
        this.codeValidated = false;
      }
      this.form.get('partnerCode')?.updateValueAndValidity();
    });
  }

  isValidForm(): boolean {
    if (this.form.invalid) return false;

    const referralSource = this.form.get('referralSource')?.value;
    if (referralSource === 'partner') {
      const partnerCodeControl = this.form.get('partnerCode');
      if (this.isCodeChecking) return false;
      return !!(partnerCodeControl?.valid && this.codeValidated && this.isCodeValid);
    }
    return true;
  }

  validatePartnerCode() {
    const code = this.form.get('partnerCode')?.value;
    if (!code) {
      this.codeValidated = false;
      this.isCodeValid = false;
      this.isCodeChecking = false;
      return;
    }
    this.isCodeChecking = true;
    this.registrationPageService.validatePartnerCode(code).subscribe(isValid => {
      this.isCodeValid = !!isValid;
      this.codeValidated = true;
      this.isCodeChecking = false;
      this.cdr.detectChanges();
    });
  }

  continue() {
    if (!this.isValidForm()) {
      this.form.markAllAsTouched();
      return;
    }

    const country = this.form.get('country')?.value?.name;
    const lookingFor = this.form.get('lookingFor')?.value; // 'ausbildung' | 'fachkraft'
    const domain = this.form.get('domain')?.value?.trim();
    const referralSource = this.form.get('referralSource')?.value;
    const partnerCode = this.form.get('partnerCode')?.value || null;

    const institutionCode = referralSource === 'partner' ? partnerCode : null;

    this.registrationPageService.sendPersonalInfo({
      country,
      lookingFor,
      domain,
      institutionCode
    });

    this.registrationPageService.openForm('personal_information_form');
  }

  getFlagSrc(countryOrNameOrCode: any | string): string {
    let code = '';
    if (typeof countryOrNameOrCode === 'string') {
      const byName = this.countries.find(
        (c: any) => c.name?.toLowerCase() === countryOrNameOrCode.toLowerCase()
      );
      code = byName?.alpha2Code ?? (countryOrNameOrCode.length === 2 ? countryOrNameOrCode : '');
    } else if (countryOrNameOrCode?.alpha2Code) {
      code = countryOrNameOrCode.alpha2Code;
    }
    return `assets/icons/flags/square/${(code || 'tn').toLowerCase()}.svg`;
  }
}
